import { CommandType, DistanceType } from '../types';
import { SuccessPattern } from './successPatternLearning';

type CommandModifiers = Record<CommandType, number>;

const NEUTRAL_MODIFIERS: CommandModifiers = {
  [CommandType.ADVANCE]: 1.0,
  [CommandType.RETREAT]: 1.0,
  [CommandType.WEAPON_ATTACK]: 1.0,
  [CommandType.SPECIAL_ATTACK]: 1.0,
  [CommandType.REFLECTOR]: 1.0,
  [CommandType.STANCE_A]: 1.0,
  [CommandType.STANCE_B]: 1.0,
};

/**
 * メタカウンターマップ
 *
 * 「AIが〇〇で成功 → 相手は△△で対策してくるはず → ☆☆で裏をかく」
 *
 * WEAPON成功 → 相手はRETREAT/REFLECTOR → ADVANCE/SPECIAL_ATTACKで裏をかく
 * SPECIAL成功 → 相手はREFLECTOR → WEAPON_ATTACK/ADVANCEで裏をかく
 * ADVANCE成功 → 相手はRETREAT → SPECIAL_ATTACK/RETREATで裏をかく
 * RETREAT成功 → 相手はADVANCE → WEAPON_ATTACK/ADVANCEで裏をかく
 * REFLECTOR成功 → 相手はWEAPON切替 → SPECIAL_ATTACK/WEAPONで裏をかく
 */
const META_COUNTER_MAP: Record<CommandType, CommandType[]> = {
  [CommandType.WEAPON_ATTACK]: [CommandType.SPECIAL_ATTACK, CommandType.ADVANCE],
  [CommandType.SPECIAL_ATTACK]: [CommandType.WEAPON_ATTACK, CommandType.ADVANCE],
  [CommandType.ADVANCE]: [CommandType.SPECIAL_ATTACK, CommandType.RETREAT],
  [CommandType.RETREAT]: [CommandType.WEAPON_ATTACK, CommandType.ADVANCE],
  [CommandType.REFLECTOR]: [CommandType.SPECIAL_ATTACK, CommandType.WEAPON_ATTACK],
  [CommandType.STANCE_A]: [CommandType.WEAPON_ATTACK, CommandType.SPECIAL_ATTACK],
  [CommandType.STANCE_B]: [CommandType.WEAPON_ATTACK, CommandType.SPECIAL_ATTACK],
};

const MIN_MODIFIER = 0.5;
const MAX_MODIFIER = 2.0;
const SCALE = 0.1;

function clampModifier(value: number): number {
  return Math.max(MIN_MODIFIER, Math.min(MAX_MODIFIER, value));
}

/**
 * メタ読みモディファイア
 *
 * 直近の成功パターンから「相手がどう対策してくるか」を予測し、
 * その対策の裏をかくコマンドの重みを上げる（1段階のみ）。
 *
 * @param successPatterns 成功パターンの配列（extractSuccessPatternsの結果）
 * @param currentDistance 現在の距離
 * @returns コマンドごとの重みモディファイア
 */
export function getMetaReadingModifiers(
  successPatterns: SuccessPattern[],
  currentDistance: DistanceType
): CommandModifiers {
  if (successPatterns.length === 0) {
    return { ...NEUTRAL_MODIFIERS };
  }

  // 成功コマンドの頻度を集計
  const commandFrequency: Partial<Record<CommandType, number>> = {};
  for (const pattern of successPatterns) {
    commandFrequency[pattern.command] = (commandFrequency[pattern.command] ?? 0) + 1;
  }

  // メタカウンターのスコアを集計
  const counterScores: Record<CommandType, number> = { ...NEUTRAL_MODIFIERS };

  for (const [cmd, freq] of Object.entries(commandFrequency) as [CommandType, number][]) {
    const counters = META_COUNTER_MAP[cmd];
    if (!counters) continue;

    for (const counter of counters) {
      counterScores[counter] += SCALE * freq;
    }
  }

  // クランプして返す
  const result: CommandModifiers = { ...NEUTRAL_MODIFIERS };
  for (const cmd of Object.values(CommandType)) {
    result[cmd] = clampModifier(counterScores[cmd]);
  }

  return result;
}
